import {
    Button,
    Card,
    Checkbox, Form,
    FormGroup,
    Grid,
    GridColumn,
    GridRow,
    Header,
    HeaderContent,
    Icon,
    Label,
    Modal,
    ModalActions,
    Placeholder,
    PlaceholderHeader,
    PlaceholderLine,
    PlaceholderParagraph,
    Segment
} from "semantic-ui-react";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {User, UserFormValues} from "../../app/models/registration/user";
import {useStore} from "../../app/stores/store";
import {toast} from "react-toastify";
import {Formik, FormikProps} from "formik";
import TextInput from "../../app/common/form/TextInput";
import {InputMasks} from "../../shared/InputMasks";
import * as yup from "yup";
import {CommonRegularExpressions} from "../../shared/CommonRegularExpressions";
import HeaderSubHeader from "semantic-ui-react/dist/commonjs/elements/Header/HeaderSubheader";

interface Props {
    show: boolean,
    userId: number,
    onClose: () => void,
}

function ManageUserDetailsModal({show, userId, onClose}: Props) {
    const [loading, setLoading] = useState(true);
    const {adminStore} = useStore();
    const commonRegex = CommonRegularExpressions;
    const [capBlockUser, setCapBlockUser] = useState<UserFormValues>(new UserFormValues());
    const [editMode, setEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        adminStore.getUser(userId)
            .then(r => {
                setCapBlockUser(r);
            })
            .catch(err => toast.error(err, {theme: "colored"}))
            .finally(() => setLoading(false));
    }, [adminStore, userId, setCapBlockUser, setLoading]);

    const handleFormSubmit = useCallback(async (user: User, setFieldError: any) => {
        setIsSubmitting(true);
        adminStore.updateUser(user)
            .then(r => {
                toast.success("Account updated successfully", {theme: "colored"});
            })
            .then(() =>{
                adminStore.getUser(userId)
                    .then(r => {
                        setCapBlockUser(r);
                    })
            })
            .catch(err => toast.error(err, {theme: "colored"}))
            .finally(() => {
                setIsSubmitting(false);
                setEditMode(false);
            });

    }, [adminStore, setIsSubmitting, setEditMode,setCapBlockUser, userId]);

    const formikRef = useRef<FormikProps<UserFormValues>>(null);

    useEffect(() => {
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    }, [capBlockUser]);


    const handleReset = useCallback(async () => {
        adminStore.getUser(userId)
            .then(r => {
                setCapBlockUser(r);
            })
            .then(() => setEditMode(false))
            .catch(err => toast.error(err, {theme: "colored"}));
    }, [adminStore, setCapBlockUser, setEditMode, userId]);

    const validationSchema = yup.object({
        firstName: yup
            .string()
            .required('First Name is required')
            .matches(commonRegex.firstNameOrLastName, 'Special characters not allowed'),
        lastName: yup
            .string()
            .required('Last Name is required')
            .matches(commonRegex.firstNameOrLastName, 'Special characters not allowed'),
        email: yup
            .string()
            .required('Email is required')
            .email('Enter a valid email'),
        phoneNumber: yup
            .string()
            .required('Phone Number is required')
            .matches(commonRegex.phoneNumber, 'Invalid Phone Number')
    });

    return (

        <Modal open={show} centered={true} closeOnEscape={true} closeOnDimmerClick={false} size='large'>
            <Modal.Header>
                <Icon name={'user'} />{capBlockUser?.firstName} {capBlockUser?.lastName} (ID:{capBlockUser?.id})
            </Modal.Header>
            <Modal.Content>
                {loading ?
                    <Placeholder>
                        <PlaceholderHeader image>
                            <PlaceholderLine length='full'/>
                            <PlaceholderLine length='full'/>
                            <PlaceholderLine length='full'/>
                        </PlaceholderHeader>
                        <PlaceholderParagraph>
                            <PlaceholderLine length='full'/>
                            <PlaceholderLine length='full'/>
                        </PlaceholderParagraph>
                    </Placeholder>
                    :
                    <>
                        <Card.Content>
                            <Grid container columns={'equal'}>
                                <GridRow>
                                    <GridColumn textAlign={'left'}>
                                        <Header as={'h3'}>
                                            <Icon name={'drivers license'}/>
                                            <HeaderContent>
                                                Account
                                                <HeaderSubHeader>
                                                    Manage user's account settings
                                                </HeaderSubHeader>
                                            </HeaderContent>
                                        </Header>
                                    </GridColumn>
                                    <GridColumn textAlign={'right'}>
                                        {capBlockUser?.disabled &&
                                            <Label color={'red'}>Account Disabled</Label>
                                        }
                                        {editMode ?
                                            <Label content="Edit Mode" color={'red'}/>
                                            :
                                            <Label content="Read only" color={'green'}/>
                                        }
                                    </GridColumn>
                                </GridRow>
                            </Grid>
                            <Segment clearing>
                                <Formik
                                    innerRef={formikRef}
                                    validationSchema={validationSchema}
                                    enableReinitialize
                                    initialValues={capBlockUser}
                                    onSubmit={(values, {setFieldError}) => {
                                        return handleFormSubmit(values, setFieldError);
                                    }}>
                                    {({handleSubmit, setFieldValue, values}) => (
                                        <Form id='userform' className="ui form" onSubmit={handleSubmit}
                                              loading={loading}
                                              autoComplete='Off'>
                                            <Form.Group widths='equal'>
                                                <TextInput showRequired={editMode} readOnly={!editMode}
                                                           placeholder='First Name'
                                                           name='firstName'
                                                           maxLength={50}/>
                                                <TextInput readOnly={!editMode} placeholder='Middle Name'
                                                           name='middleName'
                                                           maxLength={20}/>
                                                <TextInput showRequired={editMode} readOnly={!editMode}
                                                           placeholder='Last Name' name='lastName'
                                                           maxLength={50}/>
                                            </Form.Group>
                                            <TextInput showRequired={false} readOnly={true}
                                                       placeholder='Phone Number'
                                                       name='phoneNumber'
                                                       mask={InputMasks.phoneNumber}/>

                                            <TextInput showRequired={editMode} readOnly={!editMode} placeholder='Email'
                                                       name='email'
                                                       maxLength={256}/>
                                            <FormGroup widths='equal'>
                                                <Checkbox readOnly={!editMode} name='optInSMS' checked={values.optInSMS}
                                                          label="Enable SMS-based Two-Factor Authentication (2FA)"
                                                          onChange={(e, {checked}) => {
                                                              setFieldValue("optInSMS", checked ?? false)
                                                          }} style={{fontWeight: 'bold'}}/>
                                            </FormGroup>
                                            <Form.Group widths='equal'>
                                                <Checkbox readOnly={!editMode} name='disabled' checked={values.disabled}
                                                          label="Disable User"
                                                          color='red'                                                          
                                                          onChange={(e, {checked}) => {
                                                              setFieldValue("disabled", checked ?? false)
                                                          }} style={{fontWeight: 'bold', fontColor: 'red'}}/>
                                            </Form.Group>
                                        </Form>
                                    )}
                                </Formik>
                            </Segment>
                        </Card.Content>
                    </>
                }
            </Modal.Content>
            <ModalActions>
                {editMode ?
                    <>
                        <Button form='userform' type='reset' content='Cancel'
                                onClick={() => handleReset()}></Button>
                        <Button
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            floated='right'
                            primary
                            type='submit'
                            content='Save'
                            form='userform'
                        />
                    </>
                    :
                    <>
                        <Button onClick={onClose} type='button' content='Close'/>
                        <Button onClick={(event, data) => {
                            event.preventDefault(); // Hey React, type is button...so why are you submitting?
                            setEditMode(true);
                        }} type='button' content='Edit' primary/>
                    </>
                }

            </ModalActions>
        </Modal>

    )
}

export default ManageUserDetailsModal;